import { devLog, devError } from '../utils/devLogger';
import { messaging } from '../firebase';
import { getToken, onMessage, deleteToken } from 'firebase/messaging';

class FCMService {
  constructor() {
    this.vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
    this.currentToken = null;
  }

  // Request permission and get FCM token
  async requestPermission() {
    if (!messaging) {
      devLog('FCM is not supported in this browser');
      return null;
    }

    try {
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        devLog('Notification permission granted');
        return await this.getToken();
      } else {
        devLog('Notification permission denied');
        return null;
      }
    } catch (error) {
      devError('Error requesting notification permission:', error);
      return null;
    }
  }

  // Get FCM registration token
  async getToken() {
    if (!messaging || !this.vapidKey) {
      devLog('FCM not configured properly');
      return null;
    }

    try {
      const token = await getToken(messaging, {
        vapidKey: this.vapidKey
      });

      if (token) {
        this.currentToken = token;
        
        // Store token in Firestore for the current user
        await this.storeTokenInFirestore(token);
        
        return token;
      } else {
        devLog('No registration token available');
        return null;
      }
    } catch (error) {
      devError('An error occurred while retrieving token:', error);
      return null;
    }
  }

  // Store FCM token in Firestore
  async storeTokenInFirestore(token) {
    try {
      const { auth, db } = await import('../firebase');
      const { doc, updateDoc, arrayUnion } = await import('firebase/firestore');
      
      const user = auth.currentUser;
      if (!user) {
        devLog('No authenticated user to store FCM token');
        return;
      }

      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        fcmTokens: arrayUnion({
          token,
          deviceInfo: {
            userAgent: navigator.userAgent,
            timestamp: new Date(),
            platform: navigator.platform
          }
        })
      });

      devLog('FCM token stored in Firestore');
    } catch (error) {
      devError('Error storing FCM token:', error);
    }
  }

  // Remove FCM token
  async removeToken() {
    if (!messaging) return;

    try {
      await deleteToken(messaging);
      this.currentToken = null;
      
      // Remove token from Firestore
      await this.removeTokenFromFirestore();
      
      devLog('FCM token deleted successfully');
    } catch (error) {
      devError('Error deleting FCM token:', error);
    }
  }

  // Remove token from Firestore
  async removeTokenFromFirestore() {
    try {
      const { auth, db } = await import('../firebase');
      const { doc, updateDoc, arrayRemove } = await import('firebase/firestore');
      
      const user = auth.currentUser;
      if (!user || !this.currentToken) return;

      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        fcmTokens: arrayRemove(this.currentToken)
      });

      devLog('FCM token removed from Firestore');
    } catch (error) {
      devError('Error removing FCM token from Firestore:', error);
    }
  }

  // Listen for foreground messages
  setupMessageListener(callback) {
    if (!messaging) {
      devLog('FCM messaging not available');
      return;
    }

    onMessage(messaging, (payload) => {
      devLog('Message received in foreground:', payload);
      
      // Handle the message
      callback(payload);
      
      // Show notification if browser supports it
      if ('Notification' in window && Notification.permission === 'granted') {
        const { title, body, icon } = payload.notification || {};
        
        if (title && body) {
          const notification = new Notification(title, {
            body,
            icon: icon || '/favicon.ico',
            badge: '/favicon.ico',
            data: payload.data
          });

          // Handle notification click
          notification.onclick = () => {
            window.focus();
            notification.close();
            
            // Handle navigation based on notification data
            if (payload.data) {
              this.handleNotificationClick(payload.data);
            }
          };
        }
      }
    });
  }

  // Handle notification click actions
  handleNotificationClick(data) {
    const { type, conversationId, issueId } = data;
    
    switch (type) {
      case 'new-message':
        if (conversationId) {
          // Navigate to messaging system with specific conversation
          window.location.hash = `#/messages?conversation=${conversationId}`;
        }
        break;
      
      case 'issue-assigned':
      case 'issue-created':
        if (issueId) {
          // Navigate to issue tracker
          window.location.hash = `#/issues?issue=${issueId}`;
        }
        break;
      
      case 'attendance-reminder':
        // Navigate to attendance page
        window.location.hash = '#/attendance';
        break;
      
      case 'progress-report':
        // Navigate to reports
        window.location.hash = '#/reports';
        break;
      
      default:
        // Navigate to dashboard
        window.location.hash = '#/dashboard';
        break;
    }
  }

  // Send a test notification (for development)
  async sendTestNotification() {
    if (!this.currentToken) {
      devLog('No FCM token available for test notification');
      return;
    }

    try {
      // In a real implementation, this would call your backend service
      // that sends notifications via Firebase Admin SDK
      devLog('Test notification would be sent to token:', this.currentToken);
      
      // For now, just show a local notification
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Test Notification', {
          body: 'This is a test push notification from the LMS',
          icon: '/favicon.ico'
        });
      }
    } catch (error) {
      devError('Error sending test notification:', error);
    }
  }

  // Subscribe to topic (for group notifications)
  async subscribeToTopic(topic) {
    if (!this.currentToken) {
      devLog('No FCM token available for topic subscription');
      return;
    }

    try {
      // In a real implementation, this would call your backend service
      // that manages topic subscriptions via Firebase Admin SDK
      devLog(`Subscribed to topic: ${topic}`);
    } catch (error) {
      devError('Error subscribing to topic:', error);
    }
  }

  // Unsubscribe from topic
  async unsubscribeFromTopic(topic) {
    if (!this.currentToken) {
      devLog('No FCM token available for topic unsubscription');
      return;
    }

    try {
      // In a real implementation, this would call your backend service
      devLog(`Unsubscribed from topic: ${topic}`);
    } catch (error) {
      devError('Error unsubscribing from topic:', error);
    }
  }

  // Get current token
  getCurrentToken() {
    return this.currentToken;
  }

  // Check if FCM is supported
  isSupported() {
    return !!messaging && 'Notification' in window;
  }
}

const fcmService = new FCMService();
export default fcmService;